/**
 * The pieces every /field/downhole screen draws the same way, phone first:
 * a page title, a card, a full-width action button, and the three load states
 * (loading, failed — never shown as "none" — and genuinely empty).
 */
import React from 'react';
import { Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';

export function FieldTitle({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="mb-4">
      {eyebrow && <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>}
      <h1 className="text-xl font-extrabold tracking-tight text-white">{title}</h1>
      {children}
    </div>
  );
}

export function FieldCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-fieldiq-border bg-fieldiq-card ${className}`}>{children}</div>;
}

export function FieldSectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 mt-5 text-[0.7rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{children}</h2>;
}

/** A primary action, thumb-sized. */
export const FIELD_PRIMARY =
  'fi-action fi-action--standard fi-action--primary flex h-12 w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50';
/** A secondary action, same size. */
export const FIELD_SECONDARY =
  'flex h-12 w-full items-center justify-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-card text-sm font-bold text-fieldiq-text disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Loading / failed / content. A failed read says what happened in the place
 * the content would have been — it is never the empty state.
 *
 * A FAILED REFETCH IS NOT A FAILED READ. Once the content has loaded, a
 * background refetch that fails (a dead zone, a window refocus) keeps the
 * content and says it may be out of date. Swapping it for the notice unmounted
 * everything inside — an open Log run or Close run form with it, half typed.
 */
export function FieldQueryState({
  query,
  subject,
  children,
}: {
  query: Parameters<typeof queryLoadFailure>[0] & { isLoading: boolean; data?: unknown };
  subject: string;
  children: React.ReactNode;
}) {
  const failure = queryLoadFailure(query);
  // Paused for the network before any attempt failed: say so, rather than spin
  // for as long as the dead zone lasts.
  if (!failure && query.data === undefined && query.fetchStatus === 'paused') {
    return (
      <FieldCard>
        <p className="px-4 py-8 text-center text-sm text-fieldiq-text">You are offline. This will load when the connection is back.</p>
      </FieldCard>
    );
  }
  // No data and no failure is still loading — including a query paused for
  // the network, which reports isLoading false. Rendering the children there
  // would turn "not arrived" into "none".
  if (!failure && (query.isLoading || query.data === undefined)) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label={`Loading ${subject}`} />
      </div>
    );
  }
  if (failure && query.data === undefined) {
    return (
      <FieldCard>
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject={subject} />
      </FieldCard>
    );
  }
  return (
    <>
      {failure && (
        <p role="status" className="mb-2 text-xs text-fieldiq-muted">
          Could not refresh {subject} just now — showing what loaded earlier.
        </p>
      )}
      {children}
    </>
  );
}

export function FieldEmpty({ children }: { children: React.ReactNode }) {
  return (
    <FieldCard>
      <p className="px-4 py-8 text-center text-sm text-fieldiq-text">{children}</p>
    </FieldCard>
  );
}
